import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { catchError, forkJoin, of } from 'rxjs';
import { CentrexSdaModel } from 'src/app/models/centrex-sda.model';
import { ServicesDisplayModel } from 'src/app/models/service-display.model';
import { GumOrganizationsService } from 'src/app/services/gumservices/gum-organizations.service';
import { NexusServices } from 'src/app/services/nexus-services.service';
import { MatIconModule } from '@angular/material/icon';
import { ServicesSdaDialogComponent } from './services-sda-dialog/services-sda-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { Organization } from 'src/app/models/organization.model';
import { MvnoMetricsModel } from 'src/app/models/mvno-metrics.model';
import { ServiceMvnoDialogComponent } from './service-mvno-dialog/service-mvno-dialog.component';

@Component({
  selector: 'app-nexus-services',
  templateUrl: './nexus-services.component.html',
  styleUrl: './nexus-services.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatTableModule, MatIconModule, MatButtonModule],
})
export class ServicesComponent implements OnInit {
  currentOrganization: Organization | null = null;
  dataSource: ServicesDisplayModel[] = [];
  loading = false;

  constructor(
    private nexusServices: NexusServices,
    private gumOrganizationsService: GumOrganizationsService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.currentOrganization =
      this.gumOrganizationsService.getCurrentOrganization();
    this.loadServices();

    this.gumOrganizationsService.currentOrganizationChange$.subscribe((org) => {
      this.currentOrganization = org;
      this.loadServices();
    });
  }

  loadServices(): void {
    this.loading = true;

    if (!this.currentOrganization) {
      this.loading = false;
      return;
    }

    forkJoin({
      ng: this.nexusServices
        .getCentrexNgSdas(this.currentOrganization.id)
        .pipe(catchError(() => of([]))),

      enreach: this.nexusServices
        .getEnreachSdas(this.currentOrganization.id)
        .pipe(catchError(() => of([]))),

      mvno: this.nexusServices
        .getMvnoMetrics(this.currentOrganization.id)
        .pipe(catchError(() => of(null))),
    }).subscribe(({ ng, enreach, mvno }) => {
      const data: ServicesDisplayModel[] = [];

      const markPlatform = (
        sdas: CentrexSdaModel[],
        platform: string
      ): CentrexSdaModel[] => sdas.map((s) => ({ ...s, platform }));

      // NG
      const ngMarked = markPlatform(ng, 'Centrex NG');
      if (ngMarked.length > 0) {
        const uniqueNgClients = new Set(ngMarked.map((s) => s.organizationId))
          .size;
        data.push({
          nom: 'Centrex NG',
          nbClients: uniqueNgClients,
          metrics: `${ngMarked.length} SDA`,
          statut: 'Actif',
          sdas: ngMarked,
        });
      }

      // ENREACH
      const enreachMarked = markPlatform(enreach, 'Centrex Enreach');
      if (enreachMarked.length > 0) {
        const uniqueEnreachClients = new Set(
          enreachMarked.map((s) => s.organizationId)
        ).size;
        data.push({
          nom: 'Centrex Enreach',
          nbClients: uniqueEnreachClients,
          metrics: `${enreachMarked.length} SDA`,
          statut: 'Actif',
          sdas: enreachMarked,
        });
      }

      // MVNO
      data.push({
        nom: 'MVNO',
        nbClients: mvno ? mvno.nbLines : '-', // = nombre de lignes
        metrics: mvno ? `${mvno.nbLines} lignes` : '- -',
        statut: mvno ? 'Actif' : 'N/A',
        sdas: [], // MVNO n'a pas de SDA
        mvnoMetrics: mvno ?? null, // facultatif si tu veux passer la donnée au dialog
      });

      this.dataSource = data;
      this.loading = false;
    });
  }

  openDialog(row: ServicesDisplayModel): void {
    if (row.nom === 'MVNO') {
      this.dialog.open(ServiceMvnoDialogComponent, {
        width: '900px',
        data: {
          mvno: row.mvnoMetrics,
          organization: this.currentOrganization,
        },
      });
      return;
    }

    // Sinon Centrex
    this.dialog.open(ServicesSdaDialogComponent, {
      width: '900px',
      data: row.sdas,
    });
  }
}
